import React, { FC, useEffect, useState } from 'react'
import { RevenueType } from 'types/revenue'
import { RevenueListHeader, RevenueListItem } from 'components/List'
import { dateToString, parseToDate } from 'lib/helper'
import isSameDay from 'date-fns/isSameDay'
import { sumRevenueOriginalPrice, sumRevenuePrice } from 'lib/revenue'
import { HorizontalLine } from 'components/Utility'

type Props = {
  accountingType: 'order' | 'deliver'
  expandStatus: string
  resetExpandedStatus: () => void
  revenueList: RevenueType[]
}

const RevenueList: FC<Props> = (props) => {
  const { accountingType, expandStatus, resetExpandedStatus, revenueList } = props

  const [dates, setDates] = useState<Date[]>([])

  useEffect(() => {
    const _dates: Date[] = []

    for (let i = 0; i < revenueList.length; i = (i + 1) | 0) {
      const revenue: RevenueType = revenueList[i]
      if (accountingType === 'order') {
        const orderedDate = revenue.checkout_at.toDate()
        if (!_dates.some((date) => isSameDay(date, orderedDate))) {
          _dates.push(orderedDate)
        }
      } else {
        const deliveredDate = parseToDate(revenue.date)
        if (!_dates.some((date) => isSameDay(date, deliveredDate))) {
          _dates.push(deliveredDate)
        }
      }
    }
    setDates([..._dates])
  }, [revenueList, accountingType])

  return (
    <div>
      {dates.length > 0 &&
        dates.map((date) => {
          // 計上が注文日か受取日かによって注文情報をまとめる
          const _revenueList = revenueList.filter((revenue) => {
            if (accountingType === 'order') {
              return isSameDay(date, revenue.checkout_at.toDate())
            } else {
              return isSameDay(date, parseToDate(revenue.date))
            }
          })

          // 同じ受取番号の注文をまとめる
          const revenueListByBasket: RevenueType[] = []

          for (let i = 0; i < _revenueList.length; i = (i + 1) | 0) {
            const revenue = _revenueList[i]
            const index = revenueListByBasket.findIndex((_revenue) => _revenue.basket_number === revenue.basket_number)
            const amount = sumRevenuePrice(revenue, true)
            const originalPrice = sumRevenueOriginalPrice(revenue, true)

            // 存在しない場合は追加
            if (index === -1) {
              revenueListByBasket.push({
                amount: amount,
                basket_number: revenue.basket_number,
                checkout_at: revenue.checkout_at,
                date: revenue.date,
                delivery_fee_amount: revenue.delivery_fee_amount || 0,
                discount_amount: revenue.discount_amount,
                frozen_fee_amount: revenue.frozen_fee_amount || 0,
                items: revenue.items,
                originalPrice,
                shopping_bags: revenue.shopping_bags ?? undefined,
                status: revenue.status,
                user_id: revenue.user_id,
              })
            } else {
              const prev = revenueListByBasket[index]
              revenueListByBasket[index] = {
                ...prev,
                amount: prev.amount + amount,
                delivery_fee_amount: prev.delivery_fee_amount + (revenue.delivery_fee_amount || 0),
                discount_amount: prev.discount_amount + revenue.discount_amount,
                frozen_fee_amount: prev.frozen_fee_amount + (revenue.frozen_fee_amount || 0),
                items: [...prev.items, ...revenue.items],
                originalPrice: prev.originalPrice + originalPrice,
                // レジ袋が存在していればマージ、一方のみ存在していればそのまま追加、両方存在していなければundefined
                shopping_bags: prev.shopping_bags
                  ? revenue.shopping_bags
                    ? [...prev.shopping_bags, ...revenue.shopping_bags]
                    : prev.shopping_bags
                  : revenue.shopping_bags
                  ? revenue.shopping_bags
                  : undefined,
              }
            }
          }

          return (
            <React.Fragment key={dateToString(date)}>
              <RevenueListHeader date={date} />
              {revenueListByBasket.map((revenue, index) => (
                <>
                  {index !== 0 && <HorizontalLine verticalMargin={0} />}
                  <RevenueListItem
                    key={`${revenue.basket_number}-${index}`}
                    expandStatus={expandStatus}
                    resetExpandedStatus={resetExpandedStatus}
                    revenue={revenue}
                  />
                </>
              ))}
            </React.Fragment>
          )
        })}
    </div>
  )
}

export default RevenueList
